import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";

import * as fb from "../../../../../../firebase/firebasejs";
import ParticipantDetailContainer from "../../ParticipantDetail/ParticipantDetailContainer";
import FolioRangeSummary from "./FolioRangeSummary";
import FolioMatrixGrid from "./FolioMatrixGrid";
import FolioSearchBox from "./FolioSearchBox";
import ReserveFolioModal from "./ReserveFolioModal";
import FolioInconsistencies from "./FolioInconsistencies";
import {
  extractAsignadosSet,
  extractReservadosSet,
  extractFolioOwners,
  listModalidadRanges,
  findDuplicateFolios,
  findFoliosFueraDeRango,
} from "./folioMatrixLogic";

export default function FolioManagementContainer() {
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventParticipants = useSelector((state) => state.registry.eventParticipants);
  const eventSubEventsRegistry = useSelector((state) => state.events.eventSubEventsRegistry);

  const eventoIndex = "evento_" + eventDetail.iDEvento;

  const [foliosSnapshot, setFoliosSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({ mode: null, folio: null, inscritoKey: null });

  const cargarFolios = useCallback(() => {
    setLoading(true);
    setError(null);
    return fb.db
      .ref(`${fb.EventosCompletos}${eventoIndex}/${fb.inscritos}${fb.folios}`)
      .once("value")
      .then((snap) => {
        setFoliosSnapshot(snap.val() || {});
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "No se pudo leer la lista de folios");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoIndex]);

  useEffect(() => {
    cargarFolios();
  }, [cargarFolios]);

  const asignados = useMemo(() => extractAsignadosSet(foliosSnapshot), [foliosSnapshot]);
  const reservados = useMemo(() => extractReservadosSet(foliosSnapshot), [foliosSnapshot]);
  const owners = useMemo(() => extractFolioOwners(foliosSnapshot), [foliosSnapshot]);
  const modalidades = useMemo(
    () => listModalidadRanges(eventSubEventsRegistry),
    [eventSubEventsRegistry]
  );
  const duplicados = useMemo(
    () => findDuplicateFolios(foliosSnapshot, eventParticipants),
    [foliosSnapshot, eventParticipants]
  );
  const foliosFueraDeRango = useMemo(
    () => findFoliosFueraDeRango(eventParticipants, modalidades),
    [eventParticipants, modalidades]
  );

  function abrirFolio(numero) {
    if (asignados.has(numero)) {
      setDialog({ mode: "athlete", folio: numero, inscritoKey: owners.get(numero) });
    } else {
      setDialog({ mode: "reserve", folio: numero, inscritoKey: null });
    }
  }

  function abrirAtleta(inscritoKey) {
    setDialog({ mode: "athlete", folio: null, inscritoKey });
  }

  function cerrarDialogo() {
    // El detalle del atleta permite reasignar/liberar su folio
    // (UpdateRaceNumber) directamente contra /inscritos/folios/, fuera del
    // control de este container — se relee la matriz al cerrar ese detalle
    // para no dejarla desactualizada hasta un refresco manual.
    const seEstabaViendoAtleta = dialog.mode === "athlete";
    setDialog({ mode: null, folio: null, inscritoKey: null });
    if (seEstabaViendoAtleta) {
      cargarFolios();
    }
  }

  function onCambioAplicado() {
    cerrarDialogo();
    cargarFolios();
  }

  return (
    <Paper style={{ padding: "20px", margin: "20px 0", textAlign: "left" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography gutterBottom variant="h5" component="h3">
          Administración de folios
        </Typography>
        <Button
          onClick={cargarFolios}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Refrescar
        </Button>
      </Box>

      {error && (
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      )}

      {loading && !foliosSnapshot ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <FolioRangeSummary modalidades={modalidades} asignados={asignados} reservados={reservados} />

          <FolioInconsistencies duplicados={duplicados} onAbrirAtleta={abrirAtleta} onLiberado={cargarFolios} eventoIndex={eventoIndex} />

          <FolioSearchBox
            asignados={asignados}
            reservados={reservados}
            modalidades={modalidades}
            onAbrirFolio={abrirFolio}
          />

          {modalidades.map((m) => (
            <FolioMatrixGrid
              key={m.codigo}
              modalidad={m}
              asignados={asignados}
              reservados={reservados}
              onAbrirFolio={abrirFolio}
              foliosFueraDeRango={foliosFueraDeRango.filter((f) => f.modalidadCodigo === m.codigo)}
              onAbrirAtleta={abrirAtleta}
            />
          ))}
        </>
      )}

      {dialog.mode === "athlete" && dialog.inscritoKey && (
        // Dialog de Material-UI en vez de AtleteDetailsModal (que usa el
        // <Modal> de react-materialize): ese componente depende del global
        // "M" de materialize-css, que no está instalado como dependencia y
        // no queda definido en esta ruta (RegistryContainerUI, basada en
        // Material-UI) — truena con "M is not defined" al abrirlo aquí.
        <Dialog open onClose={cerrarDialogo} maxWidth="md" fullWidth>
          <IconButton
            onClick={cerrarDialogo}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent style={{ padding: 0 }}>
            {/* ParticipantDetailContainer usa la clase "overflowTrue"
              (position: absolute; height:100%; width:100%), pensada para el
              contenedor con tamaño fijo que le daba el <Modal> de
              react-materialize. Sin un ancestro con altura definida se
              posiciona fuera de la vista y el diálogo se ve vacío — este div
              le da esa referencia (position:relative + altura fija). */}
            <div style={{ position: "relative", height: "75vh" }}>
              {/* ParticipantDetailContainer reenvía la prop "ParticipantDetail"
                (no la deriva ella misma de eventParticipants) hacia
                UpdateRaceNumber -> changesRegistryFunc, que lee
                ParticipantDetail.changesRegistry directamente. Sin este prop
                queda undefined y truena al reasignar folio. Mismo patrón que
                ya usa ModalBuscadorRegistry.jsx (pasa ambos props). */}
              <ParticipantDetailContainer
                ParticipantDetail={eventParticipants && eventParticipants[dialog.inscritoKey]}
                ParticipantDetailKey={dialog.inscritoKey}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {dialog.mode === "reserve" && dialog.folio !== null && (
        <ReserveFolioModal
          eventoIndex={eventoIndex}
          numero={dialog.folio}
          estaReservado={reservados.has(dialog.folio)}
          onClose={cerrarDialogo}
          onCambioAplicado={onCambioAplicado}
        />
      )}
    </Paper>
  );
}
